import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { canDelete } from '../../utils/permissions';
import InputMask from 'react-input-mask';

const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

const formatCpfCnpj = (value) => {
  const digits = onlyDigits(value);

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
      .slice(0, 14);
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .slice(0, 18);
};

const getRequestErrorMessage = (error, fallback) => {
  const apiError = error?.response?.data;
  if (apiError?.error) return apiError.error;
  if (Array.isArray(apiError?.errors) && apiError.errors.length > 0) {
    return apiError.errors[0].msg || fallback;
  }
  return fallback;
};

export default function Clientes() {
  const { usuario } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const podeExcluir = canDelete(usuario);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    rg: '',
    telefone_principal: '',
    telefone_secundario: '',
    email: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    observacoes: '',
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes', { params: { search, limit: 1000, ativo: 'true' } });
      const data = response.data.clientes || response.data || [];
      setClientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes');
      setClientes([]);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validações
      if (!formData.nome || !formData.cpf_cnpj || !formData.telefone_principal) {
        toast.error('Preencha os campos obrigatórios: Nome, CPF/CNPJ e Telefone');
        return;
      }

      const payload = {
        ...formData,
        cpf_cnpj: formatCpfCnpj(formData.cpf_cnpj),
        email: formData.email?.trim() || null,
      };

      if (editId) {
        await api.put(`/clientes/${editId}`, payload);
        toast.success('Cliente atualizado com sucesso');
      } else {
        await api.post('/clientes', payload);
        toast.success('Cliente criado com sucesso');
      }
      handleCloseDialog();
      loadClientes();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Erro ao salvar cliente'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente desativar este cliente')) {
      try {
        await api.delete(`/clientes/${id}`);
        toast.success('Cliente desativado com sucesso');
        loadClientes();
      } catch (error) {
        console.error('Erro ao desativar cliente:', error);
        toast.error(getRequestErrorMessage(error, 'Erro ao desativar cliente'));
      }
    }
  };

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditId(cliente.id);
      setFormData({
        ...cliente,
        cpf_cnpj: formatCpfCnpj(cliente.cpf_cnpj),
      });
    } else {
      setEditId(null);
      setFormData({
        nome: '',
        cpf_cnpj: '',
        rg: '',
        telefone_principal: '',
        telefone_secundario: '',
        email: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        observacoes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditId(null);
  };

  const buscarCEP = async (cep) => {
    // Remover formatação do CEP
    const cepLimpo = cep.replace(/\D/g, '');

    if (cepLimpo.length !== 8) {
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.warning('CEP não encontrado');
        return;
      }

      // Preencher os campos automaticamente
      setFormData({
        ...formData,
        cep: cep,
        endereco: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
      });

      toast.success('CEP encontrado!');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      toast.error('Erro ao buscar CEP');
    }
  };

  const handleCEPChange = (e) => {
    const novoCEP = e.target.value;
    setFormData({ ...formData, cep: novoCEP });
  };

  const handleCEPKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      buscarCEP(formData.cep);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Clientes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Novo Cliente
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Buscar cliente"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && loadClientes()}
          InputProps={{
            endAdornment: (
              <IconButton onClick={loadClientes}>
                <Search />
              </IconButton>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>RG</TableCell>
              <TableCell>Telefone</TableCell>
              <TableCell>Cidade/Estado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientes.map((cliente) => (
              <TableRow key={cliente.id}>
                <TableCell>{cliente.nome}</TableCell>
                <TableCell>{cliente.cpf_cnpj}</TableCell>
                <TableCell>{cliente.rg || '-'}</TableCell>
                <TableCell>{cliente.telefone_principal}</TableCell>
                <TableCell>
                  {cliente.cidade && cliente.estado ? `${cliente.cidade}/${cliente.estado}` : '-'}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(cliente)} color="primary">
                    <Edit />
                  </IconButton>
                  {podeExcluir && (
                    <IconButton onClick={() => handleDelete(cliente.id)} color="error">
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {clientes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum cliente encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Dados Pessoais */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Dados Pessoais
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Nome Completo"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="CPF/CNPJ"
                value={formatCpfCnpj(formData.cpf_cnpj)}
                onChange={(e) => setFormData({ ...formData, cpf_cnpj: formatCpfCnpj(e.target.value) })}
                inputProps={{ maxLength: 18 }}
                helperText="Aceita CPF ou CNPJ"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="RG"
                value={formData.rg}
                onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputMask
                mask="(99) 99999-9999"
                value={formData.telefone_principal}
                onChange={(e) => setFormData({ ...formData, telefone_principal: e.target.value })}
              >
                {() => <TextField fullWidth required label="Telefone Principal" />}
              </InputMask>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputMask
                mask="(99) 99999-9999"
                value={formData.telefone_secundario}
                onChange={(e) => setFormData({ ...formData, telefone_secundario: e.target.value })}
              >
                {() => <TextField fullWidth label="Telefone Secundário" />}
              </InputMask>
            </Grid>

            {/* Endereço */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Endereço
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputMask
                mask="99999-999"
                value={formData.cep}
                onChange={handleCEPChange}
              >
                {() => (
                  <TextField
                    fullWidth
                    label="CEP"
                    onKeyDown={handleCEPKeyPress}
                    helperText="Pressione Enter para buscar"
                  />
                )}
              </InputMask>
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco}
                onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero}
                onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Complemento"
                value={formData.complemento}
                onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bairro"
                value={formData.bairro}
                onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Estado (UF)"
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 2 }}
              />
            </Grid>

            {/* Observações */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais sobre o cliente..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}



